// src/component/AdminGuides2026.js
//
// 2026 グループ活動「ガイド」管理画面（/admin/guides-2026）
//
// 仕様：
//   - 1コレクション global：guides2026/{id}
//     { sekki, date(YYYY-MM-DD string), title, body(HTML), createdAt, updatedAt }
//   - 全2026参加ユーザーに同じ内容が見える（scope = global）
//   - 二十四節気プルダウン、日付ピッカー、Quill HTML エディタ、隣にプレビュー
//   - 一覧は日付昇順

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  InputLabel,
  FormControl,
  Divider,
  Grid,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { db, storage } from "../api/firebase";
import { AppThemeContext } from "./AppShell";

// 二十四節気
const SEKKI_LIST = [
  "立春", "雨水", "啓蟄", "春分", "清明", "穀雨",
  "立夏", "小満", "芒種", "夏至", "小暑", "大暑",
  "立秋", "処暑", "白露", "秋分", "寒露", "霜降",
  "立冬", "小雪", "大雪", "冬至", "小寒", "大寒",
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    ["link", "image", "blockquote"],
    [{ align: [] }],
    ["clean"],
  ],
};

function formatDateLabel(s) {
  if (!s) return "";
  // s expected as "YYYY-MM-DD"
  const [y, m, d] = s.split("-");
  if (!y) return s;
  return `${y}/${m}/${d}`;
}

function formatDateRange(start, end) {
  if (!start && !end) return "";
  const a = formatDateLabel(start);
  const b = formatDateLabel(end);
  if (a && b && a === b) return a;
  if (a && b) return `${a} 〜 ${b}`;
  return a || b;
}

// HTML 中の data:image base64 を Firebase Storage にアップロードして、
// src を Storage の URL に置換した HTML を返す。
async function uploadInlineBase64Images(html) {
  if (!html || !html.includes("data:image/")) return html;
  const parser = new DOMParser();
  const docNode = parser.parseFromString(
    `<div id="__root__">${html}</div>`,
    "text/html"
  );
  const imgs = docNode.querySelectorAll('img[src^="data:image"]');
  for (const img of imgs) {
    const src = img.getAttribute("src") || "";
    const match = src.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,(.+)$/);
    if (!match) continue;
    const mime = match[1];
    const ext = (mime.split("/")[1] || "png").replace("+xml", "");
    const b64 = match[2];
    // base64 → Uint8Array
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime });
    const filename = `${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 8)}.${ext}`;
    const ref = storageRef(storage, `guides2026/images/${filename}`);
    const snap = await uploadBytes(ref, blob);
    const url = await getDownloadURL(snap.ref);
    img.setAttribute("src", url);
  }
  return docNode.getElementById("__root__").innerHTML;
}

export default function AdminGuides2026() {
  const navigate = useNavigate();
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const appBg = theme.appBg || "#F5F5F9";
  const textBase = theme.railText || "rgba(40,40,60,0.9)";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";

  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [editing, setEditing] = useState(null); // null | { id?, sekki, dateStart, dateEnd, title, body }
  const [saving, setSaving] = useState(false);

  // ── load ──
  useEffect(() => {
    setLoading(true);
    // 期間開始日でソート。旧 schema (date のみ) も dateStart にフォールバックして読む。
    const q = query(collection(db, "guides2026"), orderBy("dateStart", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const arr = [];
        snap.forEach((d) => {
          const data = d.data() || {};
          arr.push({
            id: d.id,
            ...data,
            // 旧 schema 互換
            dateStart: data.dateStart || data.date || "",
            dateEnd: data.dateEnd || data.date || "",
          });
        });
        setGuides(arr);
        setLoading(false);
      },
      (err) => {
        console.error("guides2026 load error:", err);
        setError("ガイドの読み込みに失敗しました。");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const openCreate = () => {
    setEditing({
      sekki: SEKKI_LIST[0],
      dateStart: "",
      dateEnd: "",
      title: "",
      body: "",
    });
  };
  const openEdit = (g) => {
    setEditing({
      id: g.id,
      sekki: g.sekki || SEKKI_LIST[0],
      dateStart: g.dateStart || g.date || "",
      dateEnd: g.dateEnd || g.date || "",
      title: g.title || "",
      body: g.body || "",
    });
  };
  const closeEdit = () => setEditing(null);

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.dateStart || !editing.dateEnd || !editing.title) {
      setError("開始日・終了日・タイトルは必須です。");
      return;
    }
    if (editing.dateEnd < editing.dateStart) {
      setError("終了日は開始日以降にしてください。");
      return;
    }
    setSaving(true);
    setError("");
    try {
      // 本文の base64 画像は Storage にアップロードして URL に置換（1MB 上限回避）
      let normalizedBody = editing.body || "";
      try {
        normalizedBody = await uploadInlineBase64Images(normalizedBody);
        if (normalizedBody !== editing.body) {
          // 編集中の表示にも反映
          setEditing((p) => (p ? { ...p, body: normalizedBody } : p));
        }
      } catch (uploadErr) {
        console.error("inline image upload failed:", uploadErr);
        setError(
          `本文中の画像アップロードに失敗しました：${uploadErr.message || uploadErr}`
        );
        setSaving(false);
        return;
      }

      const payload = {
        sekki: editing.sekki || "",
        dateStart: editing.dateStart,
        dateEnd: editing.dateEnd,
        title: editing.title,
        body: normalizedBody,
        updatedAt: serverTimestamp(),
      };
      if (editing.id) {
        await updateDoc(doc(db, "guides2026", editing.id), payload);
        setInfo("ガイドを更新しました。");
      } else {
        await addDoc(collection(db, "guides2026"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        setInfo("ガイドを追加しました。");
      }
      setEditing(null);
    } catch (e) {
      console.error("save guide error:", e);
      setError(`保存に失敗しました：${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (g) => {
    if (!window.confirm(`「${g.title || "(無題)"}」を削除します。よろしいですか？`))
      return;
    try {
      await deleteDoc(doc(db, "guides2026", g.id));
      setInfo("削除しました。");
    } catch (e) {
      console.error("delete guide error:", e);
      setError(`削除に失敗しました：${e.message || e}`);
    }
  };

  const previewBody = useMemo(() => editing?.body || "", [editing]);

  return (
    <Box sx={{ minHeight: "100%", background: appBg, pb: 6 }}>
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 800, color: textBase, flex: 1 }}>
            2026年 ガイド管理
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={openCreate}
            sx={{
              bgcolor: accent,
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { bgcolor: accent, opacity: 0.92 },
            }}
          >
            新規追加
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {info && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo("")}>
            {info}
          </Alert>
        )}

        <Paper
          sx={{
            border: `1px solid ${border}`,
            borderRadius: 2,
            bgcolor: surface,
            overflow: "hidden",
          }}
        >
          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : guides.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: textBase, opacity: 0.6 }}>
                まだガイドがありません。「新規追加」から作成してください。
              </Typography>
            </Box>
          ) : (
            <Stack divider={<Divider sx={{ borderColor: border }} />}>
              {guides.map((g) => (
                <Stack
                  key={g.id}
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  spacing={1.5}
                  sx={{ px: 2, py: 1.5 }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <Box sx={{ minWidth: 170 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(0,0,0,0.55)",
                          fontWeight: 700,
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, monospace",
                        }}
                      >
                        {formatDateRange(g.dateStart, g.dateEnd)}
                      </Typography>
                    </Box>
                    {g.sekki && (
                      <Chip
                        size="small"
                        label={g.sekki}
                        sx={{
                          bgcolor: `${accent}10`,
                          color: accent,
                          fontWeight: 700,
                        }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: textBase, minWidth: 0 }}
                      noWrap
                    >
                      {g.title || "(無題)"}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={() => openEdit(g)}>
                      <EditRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(g)}>
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}
        </Paper>
      </Box>

      <Dialog open={!!editing} onClose={closeEdit} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>{editing?.id ? "ガイドを編集" : "ガイドを追加"}</span>
          <IconButton onClick={closeEdit} size="small">
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editing && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                  >
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>節気</InputLabel>
                      <Select
                        label="節気"
                        value={editing.sekki || ""}
                        onChange={(e) =>
                          setEditing((p) => ({ ...p, sekki: e.target.value }))
                        }
                      >
                        {SEKKI_LIST.map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      type="date"
                      label="いつから"
                      value={editing.dateStart || ""}
                      onChange={(e) =>
                        setEditing((p) => ({ ...p, dateStart: e.target.value }))
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="いつまで"
                      value={editing.dateEnd || ""}
                      onChange={(e) =>
                        setEditing((p) => ({ ...p, dateEnd: e.target.value }))
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                  <TextField
                    size="small"
                    label="タイトル"
                    value={editing.title || ""}
                    onChange={(e) =>
                      setEditing((p) => ({ ...p, title: e.target.value }))
                    }
                    fullWidth
                  />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: textBase,
                        opacity: 0.7,
                        display: "block",
                        mb: 0.5,
                        fontWeight: 700,
                      }}
                    >
                      内容
                    </Typography>
                    <Box
                      sx={{
                        "& .ql-editor": { minHeight: 280, fontSize: 14 },
                        "& .ql-container": {
                          borderBottomLeftRadius: 4,
                          borderBottomRightRadius: 4,
                        },
                      }}
                    >
                      <ReactQuill
                        theme="snow"
                        value={editing.body || ""}
                        onChange={(v) =>
                          setEditing((p) => ({ ...p, body: v }))
                        }
                        modules={QUILL_MODULES}
                      />
                    </Box>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    border: `1px solid ${border}`,
                    borderRadius: 2,
                    p: 2,
                    bgcolor: "#fafafa",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: accent,
                      letterSpacing: "0.18em",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      display: "block",
                      mb: 1,
                    }}
                  >
                    プレビュー
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ mb: 1 }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {(editing.dateStart || editing.dateEnd) && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(0,0,0,0.55)",
                          fontWeight: 700,
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, monospace",
                        }}
                      >
                        {formatDateRange(editing.dateStart, editing.dateEnd)}
                      </Typography>
                    )}
                    {editing.sekki && (
                      <Chip
                        size="small"
                        label={editing.sekki}
                        sx={{
                          bgcolor: `${accent}12`,
                          color: accent,
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </Stack>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: textBase, mb: 1.5 }}
                  >
                    {editing.title || "(タイトル未設定)"}
                  </Typography>
                  <Box
                    sx={{
                      "& img": { maxWidth: "100%", height: "auto" },
                      "& p": { mt: 0, mb: 1 },
                      "& h1, & h2, & h3": { mt: 1.5, mb: 1, fontWeight: 800 },
                      color: textBase,
                      fontSize: 14,
                      lineHeight: 1.8,
                    }}
                    dangerouslySetInnerHTML={{ __html: previewBody }}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={saving} sx={{ textTransform: "none" }}>
            キャンセル
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveRoundedIcon />}
            sx={{
              bgcolor: accent,
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { bgcolor: accent, opacity: 0.92 },
            }}
          >
            {saving ? "保存中…" : "保存"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
